import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import DashboardPage from '@/pages/DashboardPage';
import ProjectsList from '@/pages/ProjectsList';
import ProjectDetail from '@/pages/ProjectDetail';
import ChatPage from '@/pages/ChatPage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import PublicQuoteView from '@/pages/PublicQuoteView';
import AdminPage from '@/pages/AdminPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import PlanPicker from '@/pages/PlanPicker';
import BillingSuccess from '@/pages/BillingSuccess';
import AppLayout from '@/components/AppLayout';
import { Toaster } from '@/components/ui/sonner';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children, withLayout = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) { setIsAuthenticated(true); return; }
    if (!loading) setIsAuthenticated(!!user);
  }, [user, loading, location.state]);

  if (loading || isAuthenticated === null) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 text-[#1D4053] animate-spin" /></div>;
  }
  if (!isAuthenticated && !user) return <Navigate to="/" replace />;

  // Whole-app paywall: an authenticated tenant whose subscription isn't
  // `active` is locked to the billing routes until the Stripe webhook flips
  // them. `subscription_status` is populated by /auth/me — grandfathered
  // tenants report "active" so they pass unchanged.
  const status = user?.subscription_status;
  const onBillingRoute = location.pathname.startsWith('/billing/');
  if (status && status !== 'active' && !onBillingRoute) {
    return <Navigate to="/billing/plans" replace />;
  }

  if (withLayout) return <AppLayout>{children}</AppLayout>;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 text-[#1D4053] animate-spin" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/reset-password" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsList /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/:sessionId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      {/* Paywall page — renders WITHOUT AppLayout (no sidebar, no menu, no
          dashboard chrome) so an unpaid tenant has no app access whatsoever
          until Stripe confirms payment. */}
      <Route path="/billing/plans" element={<ProtectedRoute withLayout={false}><PlanPicker /></ProtectedRoute>} />
      {/* Post-Checkout success landing: polls /auth/me until the webhook
          flips the subscription to active, then forwards to /dashboard.
          Lives under /billing/* so the paywall guard lets it render even
          while subscription_status is still "none" in the brief window
          before the webhook lands. */}
      <Route path="/billing/success" element={<ProtectedRoute withLayout={false}><BillingSuccess /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/quotes/view/:shareToken" element={<PublicQuoteView />} />
      <Route path="/accept-invite/:inviteToken" element={<AcceptInvitePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
